import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { LogOut, RefreshCw, ShieldX } from 'lucide-react';
import { queryClient } from '@/app/providers';
import { Button } from '@/components/ui/button';
import { httpClient } from '@/services/api-client';
import { getClientScopes } from '@/services/auth-scope.service';
import { useAuthStore } from '@/stores/auth.store';
import { useDocumentStore } from '@/stores/document.store';
import env from '@/configs/env';
import { SessionWarning } from '@ai-doc-automation/ui';
import { applicationSession } from './application-session';
import type { ClientAuthScope } from '@/models/auth';

interface AuthContextValue {
  signOut: () => void;
  refreshToken: () => Promise<void>;
  selectScope: (scopeKey: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  user: unknown;
  signOut: () => void;
  children: ReactNode;
}

interface IdentitySnapshot {
  sub: string;
  email: string;
  displayName: string;
}

type AuthBlockReason = 'access-denied' | 'verification-failed';

const DEV_TENANT_ID = 'a1b2c3d4-0000-0000-0000-000000000001';

function getDemoToken(email: string, role: string): string {
  if (email.trim().toLowerCase() === 'multi@docautomation.io') {
    return 'dev-multi-scope-token';
  }

  switch (role) {
    case 'reviewer':
      return 'demo-reviewer-token';
    case 'viewer':
      return 'demo-viewer-token';
    case 'admin':
    default:
      return 'demo-admin-token';
  }
}

function pickScope<T extends { scopeKey: string }>(
  scopes: T[],
  preferredScopeKey: string | null,
  suggestedScopeKey?: string,
): T | null {
  if (preferredScopeKey) {
    const preferred = scopes.find((scope) => scope.scopeKey === preferredScopeKey);
    if (preferred) {
      return preferred;
    }
  }

  if (suggestedScopeKey) {
    const suggested = scopes.find((scope) => scope.scopeKey === suggestedScopeKey);
    if (suggested) {
      return suggested;
    }
  }

  return scopes[0] ?? null;
}

function shouldResetQueryCacheForAuthChange(
  nextIdentitySub: string | null,
  nextScopeKey: string | null,
): boolean {
  const { userInfo, activeScopeKey } = useAuthStore.getState();
  const previousIdentitySub = userInfo?.sub ?? null;

  return previousIdentitySub !== nextIdentitySub || activeScopeKey !== nextScopeKey;
}

function resetWorkspaceUiState() {
  const documentStore = useDocumentStore.getState();
  documentStore.setPage(1);
  documentStore.setSelectedDocumentId(null);
}

function clearWorkspaceQueryCache(refetchActive = false) {
  void queryClient.cancelQueries();
  queryClient.clear();

  if (refetchActive) {
    setTimeout(() => {
      void queryClient.invalidateQueries({ refetchType: 'active' });
    }, 0);
  }
}

function ClientAccessBlockedScreen({
  reason,
  onRetry,
  onSignOut,
}: {
  reason: AuthBlockReason;
  onRetry: () => void;
  onSignOut: () => void;
}) {
  const isAccessDenied = reason === 'access-denied';

  return (
    <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(135deg,#09162f_0%,#10224a_45%,#122a5a_100%)] px-6 py-10 font-sans text-white">
      <div className="w-full max-w-[460px] rounded-[28px] border border-white/15 bg-white/[0.96] p-7 text-slate-950 shadow-[0_32px_80px_-36px_rgba(8,15,35,0.85)]">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
          <ShieldX className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="mt-5 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Client Workspace</div>
        <h1 className="mt-1 text-[30px] font-bold tracking-tight text-slate-950">
          {isAccessDenied ? 'No workspace access' : 'Unable to verify access'}
        </h1>
        <p className="mt-3 text-[14px] leading-6 text-slate-600">
          {isAccessDenied
            ? 'Your account is not a member of any client workspace. Ask your workspace administrator for an invite, or sign out and use a different account.'
            : 'We could not confirm your workspace access. Try again, or sign out and use a different account.'}
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {!isAccessDenied && (
            <Button type="button" variant="outline" className="h-11 flex-1 rounded-xl" onClick={onRetry}>
              <RefreshCw aria-hidden="true" />
              Try again
            </Button>
          )}
          <Button type="button" className="h-11 flex-1 rounded-xl" onClick={onSignOut}>
            <LogOut aria-hidden="true" />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AuthProvider({ user, signOut, children }: AuthProviderProps) {
  const {
    setUser,
    setUserInfo,
    setLoading,
    setAvailableScopes,
    setActiveScope,
    clearScopes,
    activeScopeKey,
  } = useAuthStore();
  const [ready, setReady] = useState(false);
  const [authBlockReason, setAuthBlockReason] = useState<AuthBlockReason | null>(null);
  const isSigningOutRef = useRef(false);

  const applyScopeSelection = useCallback(
    (
      identity: IdentitySnapshot,
      scopes: ClientAuthScope[],
      selectedScope: ClientAuthScope | null,
    ) => {
      setAvailableScopes(scopes);
      setActiveScope(selectedScope);
      httpClient.setActiveTenantId(selectedScope?.tenantId ?? null);

      if (!selectedScope) {
        setUserInfo(null);
        return;
      }

      setUserInfo({
        ...identity,
        role: selectedScope.role,
        tenantId: selectedScope.tenantId,
      });
    },
    [setActiveScope, setAvailableScopes, setUserInfo],
  );

  const syncAuth = useCallback(
    async (forceRefresh = false) => {
      if (isSigningOutRef.current) {
        return;
      }

      try {
        if (env.authMode !== 'cognito') {
          const u = user as Record<string, unknown> | null;
          const email = (u?.username as string) || (u?.email as string) || 'unknown';
          const role = ((u?.role as string) || 'admin') as 'admin' | 'reviewer' | 'viewer';
          const identity: IdentitySnapshot = {
            sub: (u?.userId as string) || 'demo-sub',
            email,
            displayName: email === 'multi@docautomation.io' ? 'Multi Scope User' : email.split('@')[0],
          };

          httpClient.setToken(getDemoToken(email, role));
          setUser(user);

          try {
            const { scopes, suggestedScopeKey } = await getClientScopes();
            if (scopes.length > 0) {
              const selectedScope = pickScope(scopes, activeScopeKey, suggestedScopeKey);
              const shouldClearCache = shouldResetQueryCacheForAuthChange(
                identity.sub,
                selectedScope?.scopeKey ?? null,
              );
              applyScopeSelection(identity, scopes, selectedScope);
              setAuthBlockReason(null);
              if (shouldClearCache) {
                resetWorkspaceUiState();
                clearWorkspaceQueryCache();
              }
              return;
            }
          } catch (err) {
            console.warn('Falling back to static dev client scope:', err);
          }

          const scopes: ClientAuthScope[] = [{
            scopeKey: `admin:${DEV_TENANT_ID}`,
            appKind: 'client',
            role,
            tenantId: DEV_TENANT_ID,
            tenantName: 'Dev Tenant',
            organizationName: 'Dev Organization',
            label: `Dev Tenant ${role.charAt(0).toUpperCase()}${role.slice(1)}`,
            isDefault: true,
          }];

          const shouldClearCache = shouldResetQueryCacheForAuthChange(
            identity.sub,
            scopes[0]?.scopeKey ?? null,
          );
          applyScopeSelection(identity, scopes, scopes[0]);
          setAuthBlockReason(null);
          if (shouldClearCache) {
            resetWorkspaceUiState();
            clearWorkspaceQueryCache();
          }
          return;
        }

        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession({ forceRefresh });

        if (isSigningOutRef.current) {
          return;
        }

        const idToken = session.tokens?.idToken;

        if (!idToken) {
          clearScopes();
          httpClient.clearToken();
          httpClient.setActiveTenantId(null);
          setUserInfo(null);
          resetWorkspaceUiState();
          clearWorkspaceQueryCache();
          setAuthBlockReason('verification-failed');
          return;
        }

        httpClient.setToken(idToken.toString());
        await applicationSession.start();
        setUser(user);

        const payload = idToken.payload as Record<string, unknown>;
        const identity: IdentitySnapshot = {
          sub: (payload.sub as string) ?? '',
          email: (payload.email as string) ?? '',
          displayName: (payload.name as string) || (payload.email as string) || '',
        };
        const { scopes, suggestedScopeKey } = await getClientScopes();

        if (!applicationSession.isActive()) return;

        // Signed in to Cognito but not a member of any client workspace
        // (e.g. an admin-portal-only user). Keep the Cognito session so the
        // user can sign out from the blocked screen; clearing only local
        // state would leave them on the sign-in route with a live session.
        if (scopes.length === 0) {
          clearScopes();
          httpClient.clearToken();
          httpClient.setActiveTenantId(null);
          setUserInfo(null);
          resetWorkspaceUiState();
          clearWorkspaceQueryCache();
          setAuthBlockReason('access-denied');
          return;
        }

        const selectedScope = pickScope(scopes, activeScopeKey, suggestedScopeKey);
        const shouldClearCache = shouldResetQueryCacheForAuthChange(
          identity.sub,
          selectedScope?.scopeKey ?? null,
        );
        applyScopeSelection(identity, scopes, selectedScope);
        setAuthBlockReason(null);
        if (shouldClearCache) {
          resetWorkspaceUiState();
          clearWorkspaceQueryCache();
        }
      } catch (err) {
        console.error('Failed to sync auth session:', err);
        clearScopes();
        httpClient.clearToken();
        httpClient.setActiveTenantId(null);
        setUserInfo(null);
        resetWorkspaceUiState();
        clearWorkspaceQueryCache();
        if (env.authMode === 'cognito' && !isSigningOutRef.current) {
          setAuthBlockReason('verification-failed');
        }
      } finally {
        setLoading(false);
        setReady(true);
      }
    },
    [activeScopeKey, applyScopeSelection, clearScopes, setLoading, setUser, setUserInfo, user],
  );

  useEffect(() => {
    if (user) {
      // Deferred so syncAuth's state updates don't run synchronously in the
      // effect body (same as admin-web).
      let cancelled = false;
      void Promise.resolve().then(() => {
        if (!cancelled) {
          void syncAuth();
        }
      });
      return () => {
        cancelled = true;
      };
    } else {
      clearScopes();
      httpClient.setActiveTenantId(null);
      setUserInfo(null);
      resetWorkspaceUiState();
      clearWorkspaceQueryCache();
      setLoading(false);
      // This effect kicks off async work (syncAuth) and, in this branch,
      // updates the external Zustand auth store rather than only this
      // component's own local state. Store setters notify subscribers
      // immediately and synchronously, so running them during render (the
      // useRenderSync pattern used elsewhere in this codebase for
      // local-state resets) can update a subscribed component while a
      // different component is mid-render — a real React warning observed
      // when this was tried locally. A useEffect is the correct tool here.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReady(true);
    }
  }, [clearScopes, setLoading, setUserInfo, syncAuth, user]);

  const refreshToken = useCallback(async () => {
    await syncAuth(true);
  }, [syncAuth]);

  const selectScope = useCallback(async (scopeKey: string) => {
    const state = useAuthStore.getState();
    const selectedScope = state.availableScopes.find((scope) => scope.scopeKey === scopeKey) ?? null;
    const currentUser = state.userInfo;

    if (!selectedScope || !currentUser) {
      return;
    }

    httpClient.setActiveTenantId(selectedScope.tenantId);
    state.setActiveScope(selectedScope);
    state.setUserInfo({
      ...currentUser,
      role: selectedScope.role,
      tenantId: selectedScope.tenantId,
    });
    resetWorkspaceUiState();
    clearWorkspaceQueryCache(true);
  }, []);

  const handleSignOut = useCallback(() => {
    isSigningOutRef.current = true;
    if (env.authMode === 'cognito') applicationSession.end();
    clearWorkspaceQueryCache();
    httpClient.clearToken();
    clearScopes();
    setAuthBlockReason(null);
    useAuthStore.getState().signOut();
    signOut();
  }, [clearScopes, signOut]);

  useEffect(() => {
    if (env.authMode !== 'cognito') return;
    applicationSession.setLogout(handleSignOut);
    const onExpired = () => applicationSession.expire();
    window.addEventListener('app-session-expired', onExpired);
    return () => {
      applicationSession.setLogout(null);
      window.removeEventListener('app-session-expired', onExpired);
    };
  }, [handleSignOut]);

  if (!ready) return null;

  if (authBlockReason) {
    return (
      <ClientAccessBlockedScreen
        reason={authBlockReason}
        onRetry={() => {
          void syncAuth(true);
        }}
        onSignOut={handleSignOut}
      />
    );
  }

  return (
    <AuthContext.Provider value={{ signOut: handleSignOut, refreshToken, selectScope }}>
      {env.authMode === 'cognito' ? <SessionWarning session={applicationSession}>{children}</SessionWarning> : children}
    </AuthContext.Provider>
  );
}

export function useAuthActions() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthActions must be inside AuthProvider');
  return ctx;
}
